// Searchable bank picker with an institution profile (SWIFT/BIC, country).
// Free text still allowed — picking a known bank just fills the profile;
// typing a new name leaves SWIFT editable so unknown banks still work.
import React, { useMemo, useRef, useState } from 'react';
import { Building2, Check } from 'lucide-react';
import { searchBanks, findBank } from '../data/banks';
import { Input } from '../ui/Form';

export const BankPicker = ({ value, onChange, onProfile, placeholder = 'Search bank… e.g. Stanbic, Equity, CERBUGKA' }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);
  const results = useMemo(() => searchBanks(value).slice(0, 8), [value]);
  const profile = findBank(value);

  const pick = (bank) => {
    onChange && onChange(bank.name);
    onProfile && onProfile(bank);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange && onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter' && results[highlight]) { e.preventDefault(); pick(results[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
      />
      {open && results.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E6E4E1] rounded-md shadow-lg overflow-hidden max-h-[260px] overflow-y-auto">
          {results.map((b, i) => (
            <button
              key={b.name}
              onMouseDown={(e) => { e.preventDefault(); pick(b); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2.5 ${i === highlight ? 'bg-[#FBFAF7]' : 'bg-white'}`}
            >
              <span className="w-7 h-7 rounded-md bg-[#EFF6FF] text-[#1E40AF] flex items-center justify-center flex-shrink-0">
                <Building2 className="w-3.5 h-3.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium text-[#1F2937] truncate">{b.name}</span>
                <span className="block text-[10.5px] text-[#6B7280] font-mono truncate">{b.country} · {b.swift}{b.code ? ` · code ${b.code}` : ''}</span>
              </span>
              {String(value || '').toLowerCase() === b.name.toLowerCase() && <Check className="w-3.5 h-3.5 text-[#1F6A3A] flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
      {profile && !open && (
        <div className="text-[11px] text-[#6B7280] mt-1 font-mono">
          {profile.swift} · {profile.country}{profile.code ? ` · sort ${profile.code}` : ''} — verify with the bank before wiring
        </div>
      )}
    </div>
  );
};
